import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { FiBell } from "react-icons/fi";
import { useSelector } from "react-redux";
import { RootState } from "../../State/store";
import CampaignIcon from '@mui/icons-material/Campaign';
import { useToast } from "./use-toast";
import { Toaster } from "./toaster";
import { useNtfy } from "../ui/ntfy";

interface Notification {
  id: number;
  campaignId: number;
  workspaceId: number;
  unread: boolean;
  statusMark: string;
  notificationType: string;
  createdAt: string;
  notificationData: Record<string, string>;
  role: string | null;
}

interface NotificationBellProps {
  workspaceId: number;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ workspaceId }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isNtfyConnected, setIsNtfyConnected] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  const apiUrlAdvAcc = useSelector((state: RootState) => state.authentication.apiURL);
  const toast = useToast();

  // Initial load effect - loads existing notifications on login
  useEffect(() => {
    if (!workspaceId || !apiUrlAdvAcc || hasInitialLoad) return;

    const loadInitialNotifications = async () => {
      try {
        const [countResponse, notificationsResponse] = await Promise.all([
          axios.get(`${apiUrlAdvAcc}/GetCampaignNotificationsCount?workspaceId=${workspaceId}`),
          axios.get(`${apiUrlAdvAcc}/GetCampaignNotifications?workspaceId=${workspaceId}`)
        ]);

        if (countResponse.data.status === "Success") {
          setUnreadCount(countResponse.data.notificationCount);
        }

        if (notificationsResponse.data.status === "Success") {
          const campaignNotifications: Notification[] = notificationsResponse.data.notifications.map((campaign: any) => ({
            id: campaign.id,
            campaignId: campaign.campaignId,
            workspaceId: campaign.workspaceId,
            unread: campaign.statusMark === "Unread",
            statusMark: campaign.statusMark,
            notificationType: campaign.notificationType,
            createdAt: campaign.createdAt,
            notificationData: campaign.notificationData || {},
            role: campaign.role
          }));
          setNotifications(campaignNotifications);
        }

        setHasInitialLoad(true);
      } catch (error) {
        console.error("Error loading initial notifications:", error);
      }
    };

    loadInitialNotifications();
  }, [workspaceId, apiUrlAdvAcc, hasInitialLoad]);

  // API polling effect - only runs as fallback when ntfy is disconnected
  useEffect(() => {
    if (!workspaceId || !apiUrlAdvAcc || isNtfyConnected || !hasInitialLoad) return;

    console.log("Starting API polling fallback - ntfy disconnected");

    const fetchLiveCampaignsCount = async () => {
      try {
        const response = await axios.get(`${apiUrlAdvAcc}/GetCampaignNotificationsCount?workspaceId=${workspaceId}`);
        if (response.data.status === "Success") {
          setUnreadCount(response.data.notificationCount);
        }
      } catch (error) {
        console.error("Error fetching notifications count:", error);
      }
    };

    // Start polling immediately when ntfy disconnects
    fetchLiveCampaignsCount();
    //const interval = setInterval(fetchLiveCampaignsCount,80000); // Continue polling every 80 seconds

    return () => {
      console.log("Stopping API polling - ntfy reconnected or component unmounted");
      //clearInterval(interval);
    };
  }, [workspaceId, apiUrlAdvAcc, isNtfyConnected, hasInitialLoad]);

  const fetchLiveCampaigns = async () => {
    // If ntfy is connected, don't fetch from API
    if (isNtfyConnected) return;

    try {
      const response = await axios.get(`${apiUrlAdvAcc}/GetCampaignNotifications?workspaceId=${workspaceId}`);
      console.log("🔹 Full API Response:", response.data);

      const campaignNotifications: Notification[] = response.data.notifications.map((campaign: any) => ({
        id: campaign.id,
        campaignId: campaign.campaignId,
        workspaceId: campaign.workspaceId,
        unread: campaign.statusMark === "Unread",
        statusMark: campaign.statusMark,
        notificationType: campaign.notificationType,
        createdAt: campaign.createdAt,
        notificationData: campaign.notificationData || {},
        role: campaign.role
      }));

      const unreadNotifications = campaignNotifications.filter((n) => n.unread);
      setNotifications(campaignNotifications);
      setUnreadCount(unreadNotifications.length);
    } catch (error) {
      console.error("Error fetching campaign notifications:", error);
    }
  };

  // ntfy connection with connection status tracking
  useNtfy(`${workspaceId}_Advertiser`, (evt) => {
    // Set connection status when we receive any event
    setIsNtfyConnected(true);
    
    const type = String(evt?.type || "").toLowerCase();

    if (type === "notification_created" || (!evt.type && evt.id)) {
      const unread = (evt.statusMark ?? evt.statusmark ?? "Unread").toLowerCase() === "unread";

      try {
        const rawmsgdata = evt.message;

        // Check if it's a stringified JSON and parse it if so
        const msgdata = typeof rawmsgdata === "string" ? JSON.parse(rawmsgdata) : rawmsgdata;
        const rawnotificationdata = msgdata.notificationdata;

        // Ensure `notificationdata` is parsed if it's a string
        const notificationdata = typeof rawnotificationdata === "string" ? JSON.parse(rawnotificationdata) : rawnotificationdata;

        // Update state with the new notification message
        setNotifications((prev) => [
          {
            id: evt.id,
            campaignId: evt.campaignId ?? evt.campaignid ?? 0,
            workspaceId: evt.workspaceId ?? evt.workspaceid ?? workspaceId,
            unread,
            statusMark: evt.statusMark ?? evt.statusmark ?? (unread ? "Unread" : "Read"),
            notificationType: evt.notificationType ?? evt.notificationtype ?? "",
            createdAt: evt.createdAt ?? evt.createdat ?? new Date().toISOString(),
            notificationData: notificationdata, // Use parsed data
            role: evt.role ?? "Advertiser",
          },
          ...prev,
        ]);

        if (unread) {
          setUnreadCount((c) => c + 1);
        }
      } catch (e) {
        console.error("Failed to parse notificationData:", e, evt.notificationData);
      }

      console.log("Raw evt.notificationData:", evt.notificationData);
    }

    if (type === "notification_read" || type === "notification_bulk_read") {
      const ids: number[] = evt.ids ?? (evt.id ? [evt.id] : []);
      if (ids.length) {
        setNotifications((prev) =>
          prev.map((n) =>
            ids.includes(n.id) ? { ...n, unread: false, statusMark: "Read" } : n
          )
        );
        setUnreadCount((c) => Math.max(0, c - ids.length));
      }
    }
  });

  const markAllAsRead = async () => {
    try {
      const response = await axios.post(`${apiUrlAdvAcc}/MarkAdvertiserNotificationRead?workspaceId=${workspaceId}`);
      
      if(response.data.status==="Success"){
        setNotifications([]);
        setUnreadCount(0);
        setShowNotifications(false);
      } else {
        toast.toast({
          title: "Error",
          description: "Failed to mark all notifications as read",
        });
      }
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    }
  };

  const handleBellClick = async () => {
    // Only fetch from API if ntfy is not connected
    if (!isNtfyConnected) {
      await fetchLiveCampaigns();
    }
    
    const nextState = !showNotifications;
    setShowNotifications(nextState);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (nextState) {
      timeoutRef.current = setTimeout(() => {
        setShowNotifications(false);
      }, 80000); // Hide after 80 seconds
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Toaster/>
      <div className="relative" ref={notificationRef}>
        <div onClick={handleBellClick} className="cursor-pointer">
          <FiBell style={{ width: "16px", height: "16px", color: "#0F172A" }} />
          {unreadCount > 0 && (
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-bold px-1 rounded-full min-w-[12px] h-[12px] flex items-center justify-center leading-none">
              {unreadCount}
            </span>
          )}
        </div>

        {showNotifications && (
          <div className="z-30 mr-6 absolute mt-4 right-0 transform translate-x-12 w-64 min-w-[280px] bg-white border border-gray-300 rounded shadow-lg p-3 overflow-visible">
            {notifications.length === 0 ? (
              <p className="text-gray-500 text-xs text-center">You have no unread notifications</p>
            ) : (
              <ul className={`${expanded ? "max-h-48" : "max-h-[96px]"} overflow-y-auto pr-1 divide-y divide-gray-200`}>
                {notifications
                  .slice(0, expanded ? undefined : 2)
                  .map((notification) => {
                    const messageValues = Object.values(notification.notificationData || {});
                    const message = messageValues.length > 0 ? messageValues[0] : "You have a new notification.";

                    return (
                      <li
                        key={notification.id}
                        className={`flex items-start text-xs py-2 text-left ${
                          notification.unread ? "font-semibold text-slate-900" : "text-slate-700"
                        }`}
                      >
                        <CampaignIcon fontSize="small" className="text-blue-500 mr-2 mt-[2px]" />
                        <span className="leading-snug">{message}</span>
                      </li>
                    );
                  })}
              </ul>
            )}

            {notifications.length > 2 && (
              <div className="border-t border-gray-200 pt-2 text-center mt-2">
                <button
                  className="text-xs text-blue-600 hover:underline"
                  onClick={() => setExpanded((prev) => !prev)}
                >
                  {expanded ? "Show Less" : "Show All"}
                </button>
              </div>
            )}

            {(notifications.length <= 2 || expanded) &&
              notifications.filter((n) => n.unread).length > 0 && (
                <>
                  <hr className="my-2" />
                  <button
                    className="w-full bg-blue-500 text-white py-1 rounded text-xs hover:bg-blue-600 transition"
                    onClick={markAllAsRead}
                  >
                    Mark all as read
                  </button>
                </>
              )}
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationBell;